import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import type { AgentInsightFeedItem } from 'features/trend-detection/features/insight-agent/types'
import { formatUtcDateFromISO } from 'features/trend-detection/utils/helpers'

export interface AgentInsightCardProps {
  insight: AgentInsightFeedItem
  onClick: (insight: AgentInsightFeedItem) => void
}

/**
 * Feed card for one insight-agent insight.
 *
 * Renders title + description only — the report body is deliberately not in the
 * feed payload, so a card costs nothing extra to list.
 */
export const AgentInsightCard: React.FC<AgentInsightCardProps> = ({
  insight,
  onClick,
}) => (
  <InsightCard
    onClick={() => onClick(insight)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick(insight)
      }
    }}
    tabIndex={0}
    role="button"
  >
    <InsightTitle>{insight.title}</InsightTitle>
    <InsightSummary>{insight.description}</InsightSummary>
    <CardFooter>
      <FooterText>
        {insight.agent_name} ·{' '}
        <Tooltip content="Generated">
          <span>{formatUtcDateFromISO(insight.create_time)}</span>
        </Tooltip>
      </FooterText>
    </CardFooter>
  </InsightCard>
)

const InsightCard = styled.div`
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    border-color: ${TREND_DETECTION_PRIMARY_BLUE};
  }

  &:focus {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

const InsightTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${darkBlueFontColor};
  line-height: 1.35;
`

const InsightSummary = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
`

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FooterText = styled.div`
  font-size: 12px;
  color: #717680;
`
